import { captureException } from '@sentry/browser'

import { ErrorHandler, Injectable } from '@angular/core'

import { Platform } from '@angular/cdk/platform'

import { enGB, fi, Locale } from 'date-fns/locale'

import { DateFnsAdapter } from '@angular/material-date-fns-adapter'
import { TuettuKieli } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'

/**
 * Extends this @angular/material-date-fns-adapter
 * https://github.com/angular/components/blob/main/src/material-date-fns-adapter/adapter/date-fns-adapter.ts
 */
@Injectable()
export class LemonDateAdapter extends DateFnsAdapter {
  private static _getSupportedDateFnsLocale(kieli: TuettuKieli): Locale {
    if (kieli === 'fi') {
      return fi
    }
    if (kieli === 'en') {
      return enGB
    }
    captureException(new Error(`No support for DateFns locale ${kieli}`))
    return fi
  }

  constructor(
    protected errorHandler: ErrorHandler,
    protected dateService: DateService,
    protected platform: Platform,
    protected translationService: LemonTranslationService,
  ) {
    // DateFnsAdapter asettaa kielen itse MAT_DATE_LOCALE-arvosta kutsumalla setLocalea; se ei ole koskaan käyttänyt parametria.
    super()
  }

  setLocale(locale: any): void {
    if (typeof locale === 'string' || locale instanceof String) {
      const supportedLocale = LemonDateAdapter._getSupportedDateFnsLocale(locale as TuettuKieli)
      super.setLocale(supportedLocale ?? fi)
    } else {
      captureException(new Error(`LemonDateAdapter.setLocale called with locale ${typeof locale}: ${locale}`))
      super.setLocale(fi)
    }
  }

  parse(value: any, parseFormat: string | string[]): Date | null {
    // console.log('parse ' + parseFormat + ' lemon: ' + this.dateService.parsiPaivamaara(value, this.translationService.nykyinenKieli) + ', date-fns: ', super.parse(value, parseFormat))
    if (typeof value === 'string') {
      try {
        return this.dateService.parsiPaivamaara(value, this.translationService.nykyinenKieli)
      } catch (error) {
        console.error(error)
        return null
      }
    }
    return super.parse(value, parseFormat)
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const kuukausienNimet = this.dateService.KUUKAUSIEN_NIMET[this.translationService.nykyinenKieli]
    if (kuukausienNimet && kuukausienNimet[style]) {
      // console.log('getMonthNames ' + style + ' lemon: ' + kuukausienNimet[style] + ', date-fns: ' + super.getMonthNames(style))
      return kuukausienNimet[style]
    }
    captureException(new Error('Ei löytynyt kuukausien nimiä kielelle ' + this.translationService.nykyinenKieli + ', tyylillä ' + style))
    return super.getMonthNames(style)
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const paivienNimet = this.dateService.PAIVIEN_NIMET[this.translationService.nykyinenKieli]
    if (paivienNimet && paivienNimet[style]) {
      // console.log('getDayOfWeekNames ' + style + ' lemon: ' + paivienNimet[style] + ', date-fns: ' + super.getDayOfWeekNames(style))
      return paivienNimet[style]
    }
    captureException(new Error('Ei löytynyt päivien nimiä kielelle ' + this.translationService.nykyinenKieli + ', tyylillä ' + style))
    return super.getDayOfWeekNames(style)
  }

  format(date: Date, displayFormat: string): string {
    // console.log('format ' + displayFormat + ' lemon: ' + this.dateService.muotoilePaivaDate(date, this.translationService.nykyinenKieli) + ', date-fns: ' + super.format(date, displayFormat))
    if (displayFormat === 'P') {
      return this.dateService.muotoilePaivaDate(date, this.translationService.nykyinenKieli)
    }
    return super.format(date, displayFormat)
  }
}
